import { type VariantProps, cva } from "class-variance-authority";
import { useEffect, useRef, useState } from "react";
import { toast } from "../../feedback/Toast/show-toast";
import { CopyIcon } from "../../icons/CopyIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { IconButton } from "./IconButton";
import { LinkButton } from "./LinkButton";

export const copyButtonStyles = cva([], {
	variants: {
		intent: {
			primary: "text-primary hover:text-primary-strong",
			secondary: "text-white hover:text-white",
		},
	},
	defaultVariants: {
		intent: "primary",
	},
});

export type DSCopyButtonProps = {
	text?: string | null;
	size?: Parameters<typeof IconButton>[0]["size"];
	i18n: { copied: string; copyToClipboard: string; copyFailed: string; copyBlocked: string };
	intent?: VariantProps<typeof copyButtonStyles>["intent"];
	label?: string;
} & Pick<Parameters<typeof IconButton>[0], "onClick">;

export const DSCopyButton = ({ text, size = "xs", i18n, intent = "primary", label, onClick }: DSCopyButtonProps) => {
	const [hasCopied, setHasCopied] = useState(false);
	const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const { copied, copyToClipboard, copyFailed, copyBlocked } = i18n;

	useEffect(() => () => clearTimeout(resetTimerRef.current), []);

	const copy = (text?: string | null) => {
		if (!text || !navigator.clipboard?.writeText) {
			return;
		}
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setHasCopied(true);
				clearTimeout(resetTimerRef.current);
				resetTimerRef.current = setTimeout(() => setHasCopied(false), 1500);
			})
			.catch((error: unknown) => {
				toast({
					variant: "error",
					title: copyFailed,
					description:
						error instanceof DOMException && error.name === "NotAllowedError" ? copyBlocked : undefined,
				});
			});
	};

	return (
		<Tooltip key={hasCopied ? "copied" : "idle"} open={hasCopied ? true : undefined}>
			<TooltipTrigger>
				{!label ? (
					<IconButton
						aria-label={copyToClipboard}
						size={size}
						intent="tertiary"
						type="button"
						className={copyButtonStyles({ intent })}
						onClick={(e) => {
							copy(text);
							onClick?.(e);
						}}
						icon={CopyIcon}
						tooltip={false}
					/>
				) : (
					<LinkButton
						type="button"
						size="sm"
						className="gap-1"
						leftIcon={CopyIcon}
						onClick={(e) => {
							copy(text);
							onClick?.(e);
						}}
					>
						{label}
					</LinkButton>
				)}
			</TooltipTrigger>
			<TooltipContent>{hasCopied ? copied : copyToClipboard}</TooltipContent>
		</Tooltip>
	);
};
